// Copyright (c) 2019-24 Stax·ai, Inc.
// See LICENSE.md file in project root directory
import React from 'react';
import { toast } from 'react-toastify';
import { withRouter } from 'react-router-dom';
import ImportFileProvider from './FileProvider';
import '../../../styles/import.css';
import api from '../../../api';

const SCRIPT_TEMPLATE = "def run(path): \n    return True";

class ImportEgnyte extends ImportFileProvider {
    // State from ImportFileProvider 
    // state = {
    //     authenticated, 
    //     configurations,
    //     config, 
    //     edit, 
    //     oauth, 
    //     folders, 
    //     expanded,
    //     stacks,
    //     template,
    // }

    state = {
        ...this.state, // Spreads ImportFileProvider into state... 
        username: '',
        password: '',
        clientId: "",
        clientSecret: "",
        domain: "",
        loading: false, 
        connectionIdx: null, 
    }

    scriptTemplate = SCRIPT_TEMPLATE; 

    loadConfig = async () => {

        // loading all exisitng stacks for this team in the state...
        const res = await api.get('/stack/all?minimal=true');
        if (res && res.success && res.stacks)
            this.setState({ stacks: res.stacks });
        else return toast.error("Failed to load stacks list");

        const { connectionIdx } = this.props.data; 
        const config = this.props.team.sync?.egnyte[connectionIdx] || {
            domain: '',
            oauth: '',
            configurations: [],
            authenticated: false, 
        };

        this.setState({
            ...config, 
            configurations: config.folders || [],
            scriptTemplate: SCRIPT_TEMPLATE, 
        });
    }
    
    saveConfig = async () => {

        const sync = this.props.team?.sync?? {}; 
        const connections = sync?.egnyte??[];
        const connectionIdx = this.state.connectionIdx??this.props.data?.connectionIdx ?? connections.length; 

        const connection = {
            user: this.props.user._id,
            domain: this.state.domain, 
            oauth: this.state.oauth,
            ...(this.state.authenticated ? (connections[connectionIdx] || {}) : {}),
            folders: this.state.configurations,
            moveUploadedFiles: this.state.moveUploadedFiles,
        };

        if(connections.length === 0)
            connections.push(connection);
        else
            connections[connectionIdx] = connection; 

        

        // Creating stacks for all selected...
        for (let folder of connection.folders) {

            // Check if the stack already exists...
            const stack = this.state.stacks.find((stack) => stack.path === folder.name);

            // if its already there, skip adding it again...
            if (stack) {
                continue;
            }

            const res = await api.post('/stack/create', { stackPath: folder.name});

            if (!res || !res.stack) {
                toast.error("Failed to create stack: " + folder.source);
                return this.props.loaded();
            }
            else{
                const destination = res.stack._id;
                // update the destination stack id in the folder...
                folder.destination = destination;
                // update the folder in the connection...
                connection.folders = connection.folders.map((f) => {
                    if(f.name === folder.name)
                        return folder;
                    return f; 
                });
            }

            this.setState({
                stacks: [...this.state.stacks, res.stack]
            });
            
        }

        // Save the team with the updated config...
        sync.egnyte = connections;    
        await this.props.updateTeam({sync});
        this.setState({connectionIdx: connectionIdx});

        // Force a manual sync now
        const res = await api.get('/sync/connections?source=egnyte');
        if (res && res.connections && res.connections.length)
                            await api.post('/sync/manual', {
                                connectionId: res.connections[connectionIdx >= 0 ? connectionIdx : connections.length-1]._id,
                                source: "egnyte"
                            });

        toast.info("Your Egnyte files will start syncing shortly");
        this.props.loaded(); 
        this.props.modal();
        
    }

    authenticate = async() => {
        this.props.loading(9999); 
        //1. Do we have oauth and domain? 
        if(this.state.oauth && this.state.domain){
            this.setState({
                authenticated: true,
            });
            return; 
        }

        const {clientId, clientSecret, domain, username, password} = this.state; 

        if(!clientId || !clientSecret || !domain || !username || !password)
            return;  
        
        const res = await api.post('/sync/egnyte/token', {clientId, clientSecret, domain, username, password});

        if(!res.success)
            return toast.error("Error authenticating with Egnyte");

        this.setState({
            authenticated: true,
            oauth: res.oauth,
        });
        await this.saveConfig(); 

        this.loadRoot(); 
        this.props.loaded(); 
    }

    loadRoot = async() => {
        this.setState({loading: true});
        const root = await this.listFolders("");
        this.setState({loading: false, folders: root?.folders??[]});
    }

    listFolders = async(path) => {
        if(!this.state.authenticated)
            return 

        let hasMore = true; 
        let limit = 100; 
        let page = 0; 
        const items = []; 
        while(hasMore){
            const res = await api.post('/sync/egnyte/list', {
                oauth: this.state.oauth,
                path: path,
                resultLimit: limit,
                resultOffset: page * limit,
                page: page,
            });

            if(!res.success)
                return toast.error("Failed to load folders from Egnyte");

            if (res && res.success && res.connection && res.connection.moveUploadedFiles !== undefined) {
                this.setState({ moveUploadedFiles : res.connection.moveUploadedFiles});
            }

            hasMore = res?.data?.total_count > (page + 1) * limit; 
            page++;
            items.push(...res.data?.folders??[]);
        }

        // Map all of the folders... 
        const folders = items.map((item) => {
            if(!item.is_folder)
                return null;
            return {
                name: item.name,
                path: this.removeFirstSlash(item.path), 
                displayName: item.name, 
                _id: item.folder_id,
                loadChildren: async() => {
                    const children = await this.listFolders(item.path)
                    this.setState({
                        folders: [...this.state.folders, ...children?.folders]
                    });
                    this.loaded = true; 
                }
            }
        }).filter((folder, index, self) =>
            index === self.findIndex((f) => (
                f.path === folder.path
            ))
        );

        return {folders}
    }

    renderDescription = () => {
        return(
            <div>
                <h1>Sync documents from Egnyte</h1>

                <p>
                    Stax·ai will make stacks for each Egnyte folder you select below and mirror the contents of these folders into your new stacks.
                </p><br />
            </div>
        )
    }


    renderContent = () => {
        return (
        <>
            {!this.state.authenticated && 
            <>
                <div
                    className='modal-content modal-center'
                >
                    {this.renderDescription()}
                    <p>
                        To get started, please enter your Egnyte domain below.
                    </p>
                    <br /> 
                    <h2>Configure connection settings</h2>
                    <form
                        className='import-config-form'
                        onSubmit={this.saveConfig}
                    >
                        <div
                            className='config'
                        >
                            <label>Username</label>
                            <input 
                                type='text'
                                className='config-field-string'
                                placeholder="Enter your Egnyte username..."
                                value={this.state.username}
                                onChange={(e) => this.setState({username: e.target.value})}
                            />
                            <div
                                className='hint'
                                style={{
                                    paddingTop: 4
                                }}
                            >
                                <p>
                                Enter the username for the Stax.ai Service account you created in Egnyte.
                                </p>
                            </div>

                            <label>Password</label>
                            <input 
                                type='password'
                                className='config-field-string'
                                placeholder="Enter your Egnyte domain..."
                                value={this.state.password}
                                onChange={(e) => this.setState({password: e.target.value})}
                            />
                            <div
                                className='hint'
                                style={{
                                    paddingTop: 4
                                }}
                            >
                                <p>
                                Enter the password for the Stax.ai Service account you created in Egnyte.
                                </p>
                            </div>

                            <label>Domain</label>
                            <input 
                                type='text'
                                className='config-field-string'
                                placeholder="Enter your Egnyte domain..."
                                value={this.state.domain}
                                onChange={(e) => this.setState({domain: e.target.value})}
                            />
                            <div
                                className='hint'
                                style={{
                                    paddingTop: 4
                                }}
                            >
                                <p>
                                For example, if your Egnyte URL is https://<span style={{color: 'var(    --color-primary)'}}>example</span>.egnyte.com, your domain is <span style={{color: 'var(    --color-primary)'}}>example</span>.
                                </p>
                            </div>

                            <label>Client Id</label>
                            <input 
                                type='text'
                                className='config-field-string'
                                placeholder="Enter your Egnyte api key..."
                                value={this.state.clientId}
                                onChange={(e) => this.setState({clientId: e.target.value})}
                            />
                            <div
                                className='hint'
                                style={{
                                    paddingTop: 4
                                }}
                            >
                                <p>
                                    This is the key string that was provided to you when you registered your application.
                                    More information can be found <a href="https://helpdesk.egnyte.com/hc/en-us/articles/14455968465037-Getting-a-Key-for-Egnyte-s-Public-API" target="_blank" rel="noopener noreferrer">here</a>.
                                </p>
                            </div>

                            <label>Client Secret</label>
                            <input 
                                type='text'
                                className='config-field-string'
                                placeholder="Enter your Egnyte api key..."
                                value={this.state.clientSecret}
                                onChange={(e) => this.setState({clientSecret: e.target.value})}
                            />
                            <div
                                className='hint'
                                style={{
                                    paddingTop: 4
                                }}
                            >
                                <p>
                                    This is the secret key that was provided with your key to you when you registered your application
                                    You need to register an app in The Egnyte Developer Portal to get an API key.
                                </p>
                            </div>
                        </div>


                        <input
                            className="import-config-button"
                            value="Connect"
                            type='submit'
                            onClick={async(e) => {
                                e.preventDefault(); 
                                e.stopPropagation(); 
                                // Save the team with the update config... 
                                // await this.saveConfig();
                                await this.authenticate(); 
                            }}
                        />
                        <div style={{ clear: 'both' }} />

                    </form>
                </div>
            </>
            }
            {
                this.state.authenticated && <>{this.renderConfigEditor()}</>
            }
        </>
        )
    }
}

export default withRouter(ImportEgnyte);
